// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/fichaTecnicaService.ts
//  Ficha Técnica / BOM versionada (pcp_formula).
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type { PaginadoResponse } from '../types';

const BASE = '/api/fichas-tecnicas';

export interface FichaTecnicaItem {
  id: number;
  materiaPrimaId: number;
  unidadeId: number;
  isMateriaPrimaPrincipal: boolean;
  quantidade: number;
  custoUnitario: number;
  custoTotal: number;
  percentualComposicao: number;
}

export interface FichaTecnicaHistorico {
  id: number;
  descricao?: string;
  dtUltimaAtualizacao: string;
  usuarioId: number;
}

export interface FichaTecnicaResumo {
  id: number;
  produtoId: number;
  descricao?: string;
  referencia?: string;
  quantidade: number;
  rendimentoUnidade: number;
  custoTotalProducao: number;
  custoTotalUnitario: number;
  perTotalPerda: number;
  isInativo: boolean;
  dtUltimaAtualizacao: string;
  totalItens: number;
  /** Descrição do produto acabado (só na listagem). */
  produtoDescricao?: string | null;
}

export interface FichaTecnicaDetalhe extends FichaTecnicaResumo {
  unidadeId: number;
  observacao?: string;
  itens: FichaTecnicaItem[];
  historicos: FichaTecnicaHistorico[];
}

export interface FichaTecnicaItemRequest {
  materiaPrimaId: number;
  quantidade: number;
  unidadeId?: number;
  isMateriaPrimaPrincipal?: boolean;
  custoUnitario?: number;
}

export interface CriarFichaTecnicaRequest {
  produtoId: number;
  quantidade: number;
  itens: FichaTecnicaItemRequest[];
  descricao?: string;
  referencia?: string;
  rendimentoUnidade?: number;
  unidadeId?: number;
  perTotalPerda?: number;
  observacao?: string;
}

export interface AtualizarFichaTecnicaRequest extends CriarFichaTecnicaRequest {
  isInativo?: boolean;
}

export const fichaTecnicaService = {
  async getAll(
    pagina = 1,
    tamanhoPagina = 50,
    filtros: { produtoId?: number; busca?: string; situacao?: 'ativas' | 'inativas' } = {},
  ): Promise<PaginadoResponse<FichaTecnicaResumo>> {
    const { data } = await apiClient.get<PaginadoResponse<FichaTecnicaResumo>>(BASE, {
      params: {
        pagina, tamanhoPagina,
        produtoId: filtros.produtoId || undefined,
        busca:     filtros.busca || undefined,
        situacao:  filtros.situacao || undefined,
      },
    });
    return data;
  },

  async getById(id: number): Promise<FichaTecnicaDetalhe> {
    const { data } = await apiClient.get<FichaTecnicaDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarFichaTecnicaRequest): Promise<FichaTecnicaDetalhe> {
    const { data } = await apiClient.post<FichaTecnicaDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarFichaTecnicaRequest): Promise<FichaTecnicaDetalhe> {
    const { data } = await apiClient.put<FichaTecnicaDetalhe>(`${BASE}/${id}`, request);
    return data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },
};
